import { createActionGroup, emptyProps, props } from '@ngrx/store';

import {
  GrassItem,
  RpmCategory,
  RpmCategoryWrite,
  RpmItem,
  RpmItemStatus,
  RpmItemWrite,
  RpmNote,
  RpmPersonData,
  RpmWeek,
  RpmWeekReview,
  RpmYearSummary,
} from '@core/models';

/**
 * RPM actions — one page action per user action, and each write is ONE call to
 * one RPM API endpoint (`core/data/api/rpm-api.service.ts`). The server answers
 * with what the call changed and the reducer folds THAT in; nothing is applied
 * before the answer, so state only ever holds what the server accepted.
 *
 * `personaId` is the person whose plan it is — the current persona, whose code
 * addresses the call (`/rpm/{personaCode}/…`).
 */
export const RpmPageActions = createActionGroup({
  source: 'RPM Page',
  events: {
    /** A screen showing RPM opened: read the plan, the Grass Catcher, the current week and the year. */
    Entered: emptyProps(),
    /** The week navigator moved to the week starting on this Sunday. */
    'Week Opened': props<{ personaId: string; weekStartDt: string }>(),
    /** A form opened: the last attempt's refusal belongs to that attempt, not this one (§8d). */
    'Form Opened': emptyProps(),
    'Item Added': props<{ personaId: string; body: RpmItemWrite }>(),
    'Item Edited': props<{ personaId: string; itemId: string; body: RpmItemWrite }>(),
    'Item Deleted': props<{ personaId: string; itemId: string }>(),
    'Status Set': props<{ personaId: string; itemId: string; status: RpmItemStatus }>(),
    'Action Ticked': props<{ personaId: string; itemId: string; actionId: string; complete: boolean }>(),
    'Plan Reordered': props<{ personaId: string; itemIds: string[] }>(),
    'Note Added': props<{ personaId: string; itemId: string; text: string }>(),
    'Grass Promoted': props<{ personaId: string; grassItemId: string; body: RpmItemWrite }>(),
    'Review Saved': props<{ personaId: string; weekStartDt: string; accomplished: string; learned: string }>(),
    'Category Added': props<{ personaId: string; body: RpmCategoryWrite }>(),
    'Category Edited': props<{ personaId: string; categoryId: string; body: RpmCategoryWrite }>(),
    'Grass Captured': props<{ personaId: string; text: string; categoryId: string | null }>(),
    'Grass Deleted': props<{ personaId: string; grassItemId: string }>(),
  },
});

export const RpmApiActions = createActionGroup({
  source: 'RPM API',
  events: {
    'Load Success': props<{ personaId: string; data: RpmPersonData }>(),
    'Load Failure': props<{ error: string }>(),
    /** The week read answered (the navigator, or the refresh after a write). */
    'Week Loaded': props<{ personaId: string; week: RpmWeek }>(),
    /** The year summary answered (the refresh after a write). */
    'Summary Loaded': props<{ personaId: string; summary: RpmYearSummary }>(),
    /** Any write that answers one whole block: add, edit, status, tick, promote. */
    'Item Saved': props<{ personaId: string; item: RpmItem }>(),
    /** The soft delete answered 204: the block has left the plan. */
    'Item Removed': props<{ personaId: string; itemId: string }>(),
    /** The reorder answered the whole plan, in its new order. */
    'Plan Ordered': props<{ personaId: string; items: RpmItem[]; categories: RpmCategory[] }>(),
    'Note Saved': props<{ personaId: string; itemId: string; note: RpmNote }>(),
    /** The promoted item leaves the Grass Catcher as its block arrives. */
    'Grass Promoted': props<{ personaId: string; grassItemId: string; item: RpmItem }>(),
    /** `review` null: both answers were empty, so the server deleted the review. */
    'Review Saved': props<{ personaId: string; weekStartDt: string; review: RpmWeekReview | null }>(),
    'Category Saved': props<{ personaId: string; category: RpmCategory }>(),
    'Grass Captured': props<{ personaId: string; item: GrassItem }>(),
    'Grass Deleted': props<{ personaId: string; grassItemId: string }>(),
    /**
     * A write was refused. `error` is the server's reason, shown at the top of
     * the open form; a one-click write's refusal has already been shown by the
     * shared error dialog, and this only ends the in-flight state.
     */
    'Write Failure': props<{ error: string }>(),
  },
});
